import React, { useCallback, useMemo } from 'react';
import { Button, Card, FileUpload, Icon, Input, Skeleton, Textarea } from '@repo/ui';

import { numberUtil } from '@xtnd/web/shared';

import type { MatterInvoiceSummaryFile } from '../../../types';
import { AppealSummary } from '../../appeals/AppealSummary';
import { ExistingBARFilesSection } from '../FileAttachments';
import { get, isEmpty } from 'lodash';
import { useGetInvoice } from '../../../queries';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../../constants/attachments';

interface NoAppealFormProps {
  invoiceId: string;
  isOpen: boolean;
  matterDisplayNumber: string;
  clientName: string;
  invoiceDate: string;
  invoiceNumber: string;
  outstandingAmount: number;
  amountPaid: number;
  totalAmount: number;
  creditsIssued: number;
  responsibleAttorneyName: string;
  isLoadingExistingFiles: boolean;
  comment: string;
  files: File[];
  existingFiles: MatterInvoiceSummaryFile[];
  selectedExistingFiles?: MatterInvoiceSummaryFile[];
  errors: Partial<Record<'comment' | 'files', string>>;
  isLoading: boolean;
  onInputChange: (field: 'comment', value: string) => void;
  onFilesChange: (files: File[]) => void;
  onExistingFilesChange?: (files: MatterInvoiceSummaryFile[]) => void;
  onSubmit: () => void;
  setError: (field: 'comment' | 'files', error: string) => void;
}


/**
 * Skeleton component for NoAppealForm loading state.
 * Matches the layout structure of the actual form.
 */
const NoAppealFormSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Side: Form Sections */}
        <div className="flex-1 space-y-6">
          {/* Financial Information Section Skeleton */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Skeleton variant="rectangular" width={40} height={40} className="rounded-lg mr-4" />
                <Skeleton variant="text" width={180} height={28} />
              </div>
            </div>

            <div className="space-y-4">
              {/* Amount to Write Off Label */}
              <Skeleton variant="text" width={140} height={20} className="mb-2" />
              
              {/* Amount Input Field */}
              <Skeleton variant="rectangular" height={40} className="rounded-md" />

              {/* Info Banner */}
              <div className="p-3 bg-gray-50 dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Skeleton variant="text" width="100%" height={16} className="mb-1" />
                <Skeleton variant="text" width="95%" height={16} />
              </div>
            </div>
          </Card>

          {/* Submission Details Section Skeleton */}
          <Card className="p-6">
            <div className="flex items-center mb-4">
              <Skeleton variant="rectangular" width={40} height={40} className="rounded-lg mr-4" />
              <Skeleton variant="text" width={160} height={28} />
            </div>

            {/* Comment Label */}
            <Skeleton variant="text" width={80} height={20} className="mb-2" />
            
            {/* Comment Textarea */}
            <Skeleton variant="rectangular" height={100} className="rounded-md mb-4" />

            <div className="mt-4 space-y-4">
              {/* Attachments Label */}
              <div className="mb-1">
                <Skeleton variant="text" width={100} height={20} className="mb-1" />
                <Skeleton variant="text" width={280} height={14} />
              </div>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8">
                <Skeleton variant="text" width={150} height={20} className="mx-auto mb-2" />
                <Skeleton variant="text" width={100} height={16} className="mx-auto" />
              </div>

              {/* File Restrictions */}
              <div className="space-y-1">
                <Skeleton variant="text" width={200} height={14} />
                <Skeleton variant="text" width={180} height={14} />
                <Skeleton variant="text" width={160} height={14} />
              </div>
            </div>
          </Card>
        </div>

        {/* Right Side: Basic Information & Action Panel */}
        <div className="lg:w-80 space-y-6">
          {/* Basic Information Section Skeleton */}
          <Card className="p-6">
            <div className="flex items-center mb-4">
              <Skeleton variant="rectangular" width={40} height={40} className="rounded-lg mr-4" />
              <Skeleton variant="text" width={150} height={28} />
            </div>

            <div className="space-y-4">
              {/* Key-Value Pairs */}
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="flex justify-between items-center">
                  <Skeleton variant="text" width={140} height={16} />
                  <Skeleton variant="text" width={120} height={16} />
                </div>
              ))}
            </div>
          </Card>

          {/* Action Panel Skeleton */}
          <Card className="!bg-transparent p-6 rounded-2xl">
            <div className="space-y-4">
              {/* Amount to Write Off Display */}
              <div className="p-3 bg-gray-100 dark:bg-xtnd-dark-800 rounded-xl">
                <div className="flex justify-between items-center">
                  <Skeleton variant="text" width={140} height={12} />
                  <Skeleton variant="text" width={100} height={24} />
                </div>
              </div>
            </div>

            {/* Confirm Button */}
            <Skeleton variant="rectangular" height={44} className="rounded-md mt-6" />
          </Card>
        </div>
      </div>
    </div>
  );
};

/**
 * Simplified form component for no-appeal write-off entries.
 * Displays a disabled Amount to Write Off field and file upload section.
 */
export const NoAppealForm: React.FC<NoAppealFormProps> = ({
  invoiceId,
  isOpen,
  invoiceNumber,
  outstandingAmount,
  amountPaid,
  matterDisplayNumber,
  clientName,
  invoiceDate,
  totalAmount,
  creditsIssued,
  responsibleAttorneyName,
  comment,
  files,
  existingFiles,
  selectedExistingFiles = [],
  errors,
  isLoading,
  isLoadingExistingFiles,
  onInputChange,
  onFilesChange,
  onExistingFilesChange,
  onSubmit,
  setError  
}) => {

  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(invoiceId, {
    enabled: isOpen && !isEmpty(invoiceId),
  });

  const amountToWriteOff = useMemo(() => {
    const lastCollectedAmount = get(invoice, 'appeal.lastCollectedAmount', 0);
    if(lastCollectedAmount > 0) {
      return get(invoice, 'appeal.remainingAmount', 0);
    }

    return get(invoice, 'actualBalance', 0);
  }, [invoice]);

  
  // Track which existing files are selected via checkboxes
  const handleExistingFileSelection = useCallback(
    (files: MatterInvoiceSummaryFile[]) => {
      if (!onExistingFilesChange) return;
      onExistingFilesChange(files);
    },
    [onExistingFilesChange]
  );

  const isFormValid = useMemo(() => {
    const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
    if (hasError) {
      return false;
    }

    // Check if comment is provided (required)
    if (!comment || comment.trim().length === 0) {
      return false;
    }

    // Check if at least one file is present (new uploads OR selected existing files)
    if (files.length === 0 && selectedExistingFiles.length === 0) {
      return false;
    }

    return true;
  }, [errors, comment, files.length, selectedExistingFiles.length]);

  if (isInvoiceLoading || isLoadingExistingFiles) {
    return <NoAppealFormSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Side: Form */}
          <div className="flex-1 space-y-6">
            {/* Amount to Write Off Section */}
            <Card className="p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                    <Icon
                      name="HiCurrencyDollar"
                      className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                      Financial Information
                    </h2>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2">
                    Amount to Write Off
                  </label>
                  <Input
                    type="text"
                    value={numberUtil.formatCurrency(amountToWriteOff)}
                    disabled
                    fullWidth
                    className="dark:border-xtnd-dark-600 bg-gray-50 dark:bg-xtnd-dark-800"
                  />
                </div>

                {/* Explanation text */}
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon
                      name="HiInformationCircle"
                      className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0"
                    />
                    <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                      When selecting 'No Appeal', the full outstanding balance is written off.
                      Custom write-off amounts are available when using 'To Be Appealed' or 'Partial
                      Write-Off' options.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Submission Details Section */}
            <Card className="p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiDocumentText"
                    className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                  />
                </div>
                <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                  Submission Details
                </h2>
              </div>

              <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2">
                Comment <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={comment}
                onChange={e => onInputChange('comment', e.target.value)}
                disabled={isLoading}
                rows={4}
                fullWidth
                required
                error={errors.comment}
                placeholder="Comment for the resolution..."
              />

              <div className="mt-4 space-y-4">
                <label className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                  <p>
                    Attachments <span className="text-red-500">*</span>
                  </p>
                  <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                    Please include supporting documentation for this write-off.
                  </span>
                </label>
                <FileUpload
                  files={files}
                  isMultiple={true}
                  accept={AR_ATTACHMENT_ACCEPT}
                  maxSize={AR_ATTACHMENT_MAX_SIZE}
                  disabled={isLoading}
                  onChange={onFilesChange}
                  isDisplayUploadButton={false}
                  maxFileNumber={12}
                  error={errors.files}
                  setError={(error: string) => setError('files', error)}
                />

                {existingFiles && existingFiles.length > 0 && (
                  <div className="mt-6">

                    <ExistingBARFilesSection
                      existingFiles={existingFiles}
                      onSelectedFilesChange={handleExistingFileSelection}
                      columns={1}
                    />
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Side: Invoice Summary Sidebar */}
          <div className="lg:w-80 space-y-6">
            <AppealSummary
              basicInformation={{
                matterDisplayNumber,
                clientName,
                invoiceDate,
                invoiceNumber,
                totalAmount,
                amountDue: outstandingAmount,
                paid: amountPaid,
                creditsIssued,
                responsibleAttorneyName,
              }}
            />

            <Card
              className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="space-y-4">
                <div className="p-3 bg-xtnd-blue/50 rounded-xl border border-xtnd-blue/80 shadow-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                      Amount to Write Off
                    </span>
                    <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                      {numberUtil.formatCurrency(amountToWriteOff)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={!isFormValid || isLoading}
                loading={isLoading}
                fullWidth
                className="mt-6"
              >
                Confirm
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default NoAppealForm;
